import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons/faCircleCheck";
import { faCircleX } from "@fortawesome/pro-regular-svg-icons/faCircleX";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Button, CardContent, Grid, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcCard, MprcClickable, MprcContainer, MprcLink, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { IconCircleEllipsis } from "components/icons/IconCircleEllipsis";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { PublicationActionResponse } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCardContext } from "../../CardBuilder";
import { OtherActionsDrawer, OtherReportsDrawer } from "./ModerateReportsFooter";
import { ThreadMasterButton } from "./ThreadItemFooter";

///////////////////////////////////////////////////////////////////////////////////////

export const ModerateReportsThreadFooter = () => {
    const {
        pub: { title, moderateReport, interaction: { allowBlock } },
        onFeedItemChanged
    } = useCardContext();

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { showUser } = useModalUserProfile();

    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [dialog, setDialog] = useState<any>(null);

    const execute = async (actionResponse: PublicationActionResponse, messageToAuthor: string | null = null, messageToRequester: string | null = null) => {
        setSaving(true);

        try {
            var result = await api.v1.knowledgeManagement.moderatePublication({ actionId: moderateReport!.actionId, actionResponse, messageToAuthor, messageToRequester });
            onFeedItemChanged({ moderateReport: { ...moderateReport!, analysed: result.data } });
        }
        catch (e) {
            setError(e);
        }
        finally {
            setSaving(false);
        }
    };

    const showOthersActions = () => {
        setDialog(
            <OtherActionsDrawer
                isComment={moderateReport!.comment != null}
                title={title}
                allowBlock={allowBlock}
                onClose={
                    (result) => {
                        setDialog(null);
                        if (result) execute(result.status, result.messageAuthor, result.messageRequester);
                    }
                }
            />
        );
    };

    const showOthersReports = () => {
        setDialog(<OtherReportsDrawer
            actionId={moderateReport!.actionId}
            title={title}
            onClose={() => setDialog(null)}
        />);
    };


    return (
        <>
            {dialog}

            <MprcRequestIndicator type="post" visible={saving} />
            <MprcRequestErrorDialog error={error} onClose={() => setError(null)} />

            <MprcContainer>
                <MprcCard paperColor="notice">
                    <CardContent>
                        <Grid container justifyContent="center" alignItems="center" spacing={1}>
                            <Grid size="grow">
                                <MprcClickable onClick={() => showUser(moderateReport!.requester.userId)}>
                                    <MprcFeedTypography variant="authorPrimary" title={moderateReport!.requester.name}>
                                        {moderateReport!.requester.name}
                                        <span>:</span>
                                    </MprcFeedTypography>
                                </MprcClickable>
                                <MprcFeedTypography variant="authorSecondary" title={moderateReport!.requester.name}>
                                    &nbsp;{t("Feed.Card.ModerateReports.ReportedText")} {Formaters.relativeTime(moderateReport!.requestedOn)}
                                </MprcFeedTypography>
                                <MprcFeedTypography variant="authorCaption">
                                    &nbsp;{moderateReport!.reasonName}
                                </MprcFeedTypography>
                                <MprcFeedTypography variant="authorSecondary" component="div">
                                    {moderateReport!.message}
                                </MprcFeedTypography>
                            </Grid>
                            <Grid className={AppGlobalClassKeys.clickable}>
                                <Avatar
                                    src={moderateReport!.requester.thumbnailUrl}
                                    title={moderateReport!.requester.name}
                                    onClick={() => showUser(moderateReport!.requester.userId)}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </MprcCard>

                {
                    moderateReport!.otherCount > 0 &&
                    <Box mt={1} width="100%" textAlign="right">
                        <MprcLink onClick={showOthersReports}>
                            <Typography variant="captionHeader" color="info.main">
                                {t("Feed.Card.ModerateReports.SeeOthersReports", { count: moderateReport!.otherCount })}
                            </Typography>
                        </MprcLink>
                    </Box>
                }
            </MprcContainer>


            <Grid container justifyContent="space-between" alignItems="center">
                <Grid>
                    <ThreadMasterButton />
                </Grid>
                <Grid>
                    <Button variant="text" color="warning" onClick={() => execute(PublicationActionResponse.ReportExcludePublication)}>
                        {<FontAwesomeIcon icon={faCircleX} fixedWidth />}
                        <span>&nbsp;{t("Feed.Card.ModerateReports.ExcludeThreadButtonText")}</span>
                    </Button>
                </Grid>
                <Grid>
                    <Button variant="text" color="success" onClick={() => execute(PublicationActionResponse.ReportIgnore)}>
                        {<FontAwesomeIcon icon={faCircleCheck} fixedWidth />}
                        <span>&nbsp;{t("Feed.Card.ModerateReports.KeepThreadButtonText")}</span>
                    </Button>
                </Grid>
                <Grid>
                    <Button variant="text" color="muted" onClick={showOthersActions}>
                        <IconCircleEllipsis />
                        <span>&nbsp;{t("Feed.Card.ModerateReports.OtherActionsButtonText")}</span>
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};
